import React, { useState, useEffect, useRef } from 'react';
import { manageAPI, adminItemsAPI, proxyImageUrl } from '../api/client';
import StaleProductsTab from './StaleProductsTab';

function SearchDropdown({ label, value, onChange, options, loading, disabled, placeholder, renderOption, getKey, getLabel, searchText }) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  const getSearchable = searchText || getLabel;
  const filtered = search.trim()
    ? options.filter(o => getSearchable(o).toLowerCase().includes(search.toLowerCase()))
    : options;

  const handleSelect = (o) => {
    onChange(o);
    setSearch(getLabel(o));
    setOpen(false);
  };

  useEffect(() => {
    if (value) setSearch(getLabel(value));
    else setSearch('');
  }, [value]);

  return (
    <div className="mng-field">
      <label className="mng-label">{label}</label>
      <div className="mng-dropdown-wrap">
        <input
          className="mng-input"
          value={search}
          onChange={e => { setSearch(e.target.value); onChange(null); setOpen(true); }}
          onFocus={() => options.length > 0 && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 160)}
          placeholder={loading ? 'Загрузка…' : placeholder}
          disabled={disabled || loading}
          autoComplete="off"
        />
        {open && filtered.length > 0 && (
          <div className="mng-dropdown">
            {filtered.map((o, i) => (
              // getKey(o) сам по собі може повторюватись (напр. однаковий
              // supplier_code у постачальника трапляється двічі з різними
              // catalog_id) — без індексу React плутає позиції при фільтрації
              // й показує в списку старий вміст із попереднього рендеру.
              <button key={`${getKey(o)}__${i}`} type="button" className="mng-dropdown-item" onMouseDown={() => handleSelect(o)}>
                {renderOption(o)}
              </button>
            ))}
          </div>
        )}
        {open && !loading && filtered.length === 0 && search.trim() && (
          <div className="mng-dropdown"><div className="mng-dropdown-empty">Ничего не найдено</div></div>
        )}
      </div>
    </div>
  );
}

const EMPTY_FIELDS = { catalog_id: '', photo_url: '', photo_link: '', new_code: '' };

export default function ManageProductsTab() {
  const [subTab, setSubTab] = useState('catalog'); // catalog | supplier | stale | maintenance

  // ── Удаление / объединение товаров каталога ───────────────────────────────
  const [catQuery,    setCatQuery]    = useState('');
  const [catResults,  setCatResults]  = useState([]);
  const [catSearching, setCatSearching] = useState(false);
  const [catSelected, setCatSelected] = useState(null); // выбранный товар (primary для объединения)
  const [catLoading,  setCatLoading]  = useState(false);
  const [catError,    setCatError]    = useState('');

  const [mergeQuery,   setMergeQuery]   = useState('');
  const [mergeResults, setMergeResults] = useState([]);
  const [mergeList,    setMergeList]    = useState([]); // второстепенные SKU для объединения
  const [mergeOpen,    setMergeOpen]    = useState(false);

  const [actionResult, setActionResult] = useState(null); // { type: 'delete'|'merge', job_id }
  const [actionRunning, setActionRunning] = useState(false);

  useEffect(() => {
    if (!catQuery.trim()) { setCatResults([]); return; }
    setCatSearching(true);
    const t = setTimeout(() => {
      manageAPI.search(catQuery.trim(), 15)
        .then(res => setCatResults(res.data || []))
        .catch(() => setCatResults([]))
        .finally(() => setCatSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [catQuery]);

  useEffect(() => {
    if (!mergeQuery.trim()) { setMergeResults([]); return; }
    const t = setTimeout(() => {
      manageAPI.search(mergeQuery.trim(), 15)
        .then(res => setMergeResults(res.data || []))
        .catch(() => setMergeResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [mergeQuery]);

  const selectCatProduct = async (p) => {
    setCatQuery('');
    setCatResults([]);
    setActionResult(null);
    setCatError('');
    setMergeList([]);
    setCatLoading(true);
    try {
      const res = await manageAPI.getProduct(p.sku);
      setCatSelected(res.data);
    } catch (e) {
      setCatError(e?.response?.data?.detail || 'Ошибка загрузки товара');
    } finally {
      setCatLoading(false);
    }
  };

  const handleDeleteJob = async () => {
    if (!catSelected) return;
    if (!window.confirm(
      `Полностью удалить ${catSelected.sku} — «${catSelected.name}»?\n\n` +
      `Будет выполнено по шагам (видно в "Очереди"):\n` +
      `1. Архивация + удаление из КаталогТоваров\n` +
      `2. Блок Setting/ALL\n` +
      `3. Запись в "Список товаров"\n` +
      `4. Листы поставщиков + ALLDROP ART\n` +
      `5. Внешняя SQLite\n` +
      `6. Отвязка карточек на сайтах-витринах\n` +
      `7. Наша база данных\n\n` +
      `Отменить после запуска нельзя (кроме как отменить/повторить отдельные шаги в "Очереди").`
    )) return;
    setActionRunning(true);
    try {
      const res = await manageAPI.deleteProductJob(catSelected.sku);
      setActionResult({ type: 'delete', job_id: res.data.job_id, sku: catSelected.sku });
      setCatSelected(null);
    } catch (e) {
      alert('Ошибка запуска удаления: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setActionRunning(false);
    }
  };

  const handleMergeJob = async () => {
    if (!catSelected || mergeList.length === 0) return;
    if (!window.confirm(
      `Объединить ${mergeList.map(s => s.sku).join(', ')} в ${catSelected.sku}?\n\n` +
      `Товары поставщиков переносятся на ${catSelected.sku}, карточки на сайтах ` +
      `перепривязываются, а сами второстепенные артикулы (${mergeList.map(s => s.sku).join(', ')}) ` +
      `удаляются отовсюду (КаталогТоваров, блоки Setting/ALL, Список товаров, их SQLite).`
    )) return;
    setActionRunning(true);
    try {
      const res = await manageAPI.mergeJob(catSelected.sku, mergeList.map(s => s.sku));
      setActionResult({ type: 'merge', job_id: res.data.job_id, sku: catSelected.sku, secondary: mergeList.map(s => s.sku) });
      setCatSelected(null);
      setMergeList([]);
    } catch (e) {
      alert('Ошибка запуска объединения: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setActionRunning(false);
    }
  };

  const [suppliers, setSuppliers] = useState([]);
  const [supplier, setSupplier] = useState(null);
  const [articles, setArticles] = useState([]);
  const [articlesLoading, setArticlesLoading] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [fields, setFields] = useState(EMPTY_FIELDS);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [photoUploading, setPhotoUploading] = useState(false);
  const fileInputRef = useRef(null);

  // ── Переіндексація дзеркала листів постачальників ────────────────────────
  const [reindexStatus, setReindexStatus] = useState(null); // null = не питали, ще не показуємо
  const reindexPollRef = useRef(null);

  const pollReindexStatus = async () => {
    try {
      const res = await manageAPI.reindexSuppliersStatus();
      setReindexStatus(res.data);
      if (res.data.running) {
        reindexPollRef.current = setTimeout(pollReindexStatus, 3000);
      }
    } catch { /* игнор — переопросим на следующем клике */ }
  };

  useEffect(() => () => { if (reindexPollRef.current) clearTimeout(reindexPollRef.current); }, []);

  const handleReindex = async () => {
    if (!window.confirm('Запустить полную переиндексацию всех листов поставщиков?\n\nЗаймёт около 15-20 минут в фоне, можно продолжать работать.')) return;
    try {
      const res = await manageAPI.reindexSuppliers();
      if (res.data.status === 'already_running') {
        setReindexStatus(res.data.progress);
      } else {
        setReindexStatus({ running: true, suppliers_done: 0, total_suppliers: 0 });
      }
      pollReindexStatus();
    } catch (e) {
      alert('Ошибка запуска: ' + (e?.response?.data?.detail || e.message));
    }
  };

  useEffect(() => {
    manageAPI.getSuppliers().then(res => setSuppliers(res.data)).catch(() => {});
  }, []);

  const handleSupplierChange = async (s) => {
    setSupplier(s);
    setSelectedArticle(null);
    setArticles([]);
    setFields(EMPTY_FIELDS);
    setResult(null);
    setError('');
    if (!s) return;
    setArticlesLoading(true);
    try {
      const res = await manageAPI.getSupplierArticles(s);
      setArticles(res.data);
    } catch (e) { setError(e?.response?.data?.detail || 'Ошибка загрузки статей'); }
    finally { setArticlesLoading(false); }
  };

  const handleArticleChange = (a) => {
    setSelectedArticle(a);
    setResult(null);
    setError('');
    if (a) {
      setFields({ catalog_id: a.catalog_id || '', photo_url: a.photo_url || '', photo_link: a.photo_link || '', new_code: a.supplier_code || '' });
    } else {
      setFields(EMPTY_FIELDS);
    }
  };

  const setField = (key, val) => {
    setFields(f => ({ ...f, [key]: val }));
    setResult(null);
    setError('');
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoUploading(true);
    setError('');
    try {
      const res = await adminItemsAPI.uploadPhoto(file);
      setField('photo_url', res.data.url);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Ошибка загрузки фото');
    } finally {
      setPhotoUploading(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!supplier || !selectedArticle) return;
    // Отправляем только изменённые поля
    const changed = {};
    if (fields.catalog_id !== (selectedArticle.catalog_id || '') && fields.catalog_id.trim())
      changed.catalog_id = fields.catalog_id.trim();
    if (fields.photo_url !== (selectedArticle.photo_url || ''))
      changed.photo_url = fields.photo_url.trim();
    if (fields.photo_link !== (selectedArticle.photo_link || ''))
      changed.photo_link = fields.photo_link.trim();
    if (fields.new_code !== (selectedArticle.supplier_code || '') && fields.new_code.trim())
      changed.new_code = fields.new_code.trim();

    if (!Object.keys(changed).length) { setError('Нет изменений'); return; }

    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await manageAPI.updateSupplierItem(supplier, selectedArticle.supplier_code, changed);
      setResult({ job_id: res.data.job_id, changed });
      // Оптимистично обновляем локальный список (new_code меняет и сам ключ supplier_code)
      const next = { ...selectedArticle, ...changed };
      if (changed.new_code) next.supplier_code = changed.new_code;
      setSelectedArticle(next);
      setArticles(prev => prev.map(a => a.supplier_code === selectedArticle.supplier_code ? next : a));
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка изменения');
    } finally {
      setLoading(false);
    }
  };

  const u = result?.updated;
  const hasPhoto = fields.photo_url && fields.photo_url.startsWith('http');

  return (
    <div className="mng-tab">
      <div className="mng-header">
        <div className="mng-title">Управление товарами</div>
      </div>

      <div className="rt-tabs rt-tabs--sub" style={{ marginBottom: 16 }}>
        <button className={`rt-tab ${subTab === 'catalog' ? 'rt-tab--active' : ''}`}
          onClick={() => setSubTab('catalog')}>Товар каталога</button>
        <button className={`rt-tab ${subTab === 'supplier' ? 'rt-tab--active' : ''}`}
          onClick={() => setSubTab('supplier')}>Товар поставщика</button>
        <button className={`rt-tab ${subTab === 'stale' ? 'rt-tab--active' : ''}`}
          onClick={() => setSubTab('stale')}>Под удаление</button>
        <button className={`rt-tab ${subTab === 'maintenance' ? 'rt-tab--active' : ''}`}
          onClick={() => setSubTab('maintenance')}>Обслуживание</button>
      </div>

      {subTab === 'maintenance' && (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, padding: '10px 12px',
                    border: '1px solid var(--lg-border)', borderRadius: 8, flexWrap: 'wrap' }}>
        <button type="button" className="mng-btn" onClick={handleReindex}
          disabled={reindexStatus?.running}>
          {reindexStatus?.running ? '⏳ Переиндексация…' : '🔄 Переиндексировать листы поставщиков'}
        </button>
        <span style={{ fontSize: 12, opacity: 0.75 }}>
          {reindexStatus?.running
            ? `${reindexStatus.suppliers_done || 0} / ${reindexStatus.total_suppliers || '?'} — ${reindexStatus.current_supplier || ''}`
            : reindexStatus?.result
              ? `Готово: ${reindexStatus.result.suppliers_done} поставщиков, ${reindexStatus.result.pairs_cached} записей в кэше${reindexStatus.result.errors?.length ? `, ошибок: ${reindexStatus.result.errors.length}` : ''}`
              : 'Обновляет кэш расположения товаров на листах поставщиков (нужно для удаления/объединения товаров) — обычно не требуется, кэш пополняется сам при добавлении товаров'}
        </span>
      </div>
      )}

      {subTab === 'stale' && <StaleProductsTab />}

      {subTab === 'catalog' && (
      <div>
        <div className="mng-desc" style={{ marginBottom: 14 }}>
          Найдите товар каталога по артикулу или названию — удалите его полностью или объедините с другим.
        </div>

        {actionResult && (
          <div style={{ marginBottom: 12, padding: '8px 10px', borderRadius: 6, fontSize: 13,
                        background: 'rgba(34,197,94,0.12)', color: 'var(--lg-text)' }}>
            {actionResult.type === 'delete'
              ? `Запущено удаление ${actionResult.sku} — задача #${actionResult.job_id}`
              : `Запущено объединение ${actionResult.secondary.join(', ')} → ${actionResult.sku} — задача #${actionResult.job_id}`}
            {' '}(прогресс — во вкладке «Очередь»)
          </div>
        )}

        {!catSelected ? (
          <div className="mng-dropdown-wrap" style={{ maxWidth: 420 }}>
            <input
              className="mng-input"
              value={catQuery}
              onChange={e => setCatQuery(e.target.value)}
              placeholder={catSearching ? 'Поиск…' : 'Артикул или название товара…'}
            />
            {catResults.length > 0 && (
              <div className="mng-dropdown">
                {catResults.map(p => (
                  <button key={p.sku} type="button" className="mng-dropdown-item"
                    onMouseDown={() => selectCatProduct(p)}>
                    <span className="mng-article-code">{p.sku}</span>
                    <span style={{ marginLeft: 8, opacity: 0.75 }}>{p.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : catLoading ? (
          <div style={{ opacity: 0.7, fontSize: 13 }}>Загрузка…</div>
        ) : (
          <div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 14 }}>
              {catSelected.photo_url && (
                <img src={proxyImageUrl(catSelected.photo_url)} alt="" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6 }} />
              )}
              <div>
                <div style={{ fontWeight: 600 }}>{catSelected.sku} — {catSelected.name}</div>
                <div style={{ fontSize: 12, opacity: 0.75 }}>
                  {catSelected.brand} {catSelected.model} · {catSelected.color || '—'} · {catSelected.season || '—'} ·
                  {' '}поставщиков: {catSelected.suppliers?.length ?? 0}
                </div>
              </div>
              <button type="button" className="mng-btn" style={{ marginLeft: 'auto' }}
                onClick={() => { setCatSelected(null); setMergeList([]); }}>Отмена</button>
            </div>

            {catError && <div style={{ color: '#e33', marginBottom: 10 }}>{catError}</div>}

            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              <div>
                <button type="button" className="mng-btn mng-btn--danger" disabled={actionRunning}
                  onClick={handleDeleteJob}>
                  🗑 Удалить {catSelected.sku} полностью
                </button>
              </div>

              <div style={{ minWidth: 280 }}>
                <div style={{ fontSize: 13, marginBottom: 6 }}>
                  Объединить в {catSelected.sku} (второстепенные будут удалены):
                </div>
                {mergeList.map(s => (
                  <span key={s.sku} style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
                    background: 'var(--lg-bg-2, #eee)', borderRadius: 4, padding: '2px 6px', marginRight: 6, marginBottom: 6, fontSize: 12 }}>
                    {s.sku}
                    <button type="button" onClick={() => setMergeList(l => l.filter(x => x.sku !== s.sku))}
                      style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, lineHeight: 1 }}>✕</button>
                  </span>
                ))}
                <div className="mng-dropdown-wrap" style={{ maxWidth: 280 }}>
                  <input
                    className="mng-input"
                    value={mergeQuery}
                    onFocus={() => setMergeOpen(true)}
                    onBlur={() => setTimeout(() => setMergeOpen(false), 160)}
                    onChange={e => setMergeQuery(e.target.value)}
                    placeholder="Добавить второстепенный артикул…"
                  />
                  {mergeOpen && mergeResults.length > 0 && (
                    <div className="mng-dropdown">
                      {mergeResults.filter(p => p.sku !== catSelected.sku && !mergeList.some(s => s.sku === p.sku)).map(p => (
                        <button key={p.sku} type="button" className="mng-dropdown-item"
                          onMouseDown={() => { setMergeList(l => [...l, p]); setMergeQuery(''); }}>
                          <span className="mng-article-code">{p.sku}</span>
                          <span style={{ marginLeft: 8, opacity: 0.75 }}>{p.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button type="button" className="mng-btn" style={{ marginTop: 8 }}
                  disabled={mergeList.length === 0 || actionRunning}
                  onClick={handleMergeJob}>
                  🔀 Объединить ({mergeList.length})
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {subTab === 'supplier' && (
      <form className="mng-form" onSubmit={handleSubmit}>
        <div className="mng-desc" style={{ marginBottom: 14 }}>
          Обновляет артикул каталога, фото и TG-ссылку в основной таблице, ALLDROP ART и базе данных.
        </div>

        <SearchDropdown
          label="Поставщик"
          value={supplier}
          onChange={handleSupplierChange}
          options={suppliers}
          loading={false}
          disabled={false}
          placeholder="Поиск поставщика…"
          getKey={s => s}
          getLabel={s => s}
          renderOption={s => <span className="mng-supplier-name">{s}</span>}
        />

        <SearchDropdown
          label="Артикул поставщика"
          value={selectedArticle}
          onChange={handleArticleChange}
          options={articles}
          loading={articlesLoading}
          disabled={!supplier}
          placeholder={supplier ? 'Поиск по артикулу или каталогу…' : 'Сначала выберите поставщика'}
          getKey={a => a.supplier_code}
          getLabel={a => a.supplier_code}
          searchText={a => `${a.supplier_code} ${a.catalog_id || ''}`}
          renderOption={a => (
            <span className="mng-article-option">
              <span className="mng-article-code" title={a.supplier_code}>{a.supplier_code}</span>
              {a.catalog_id && <span className="mng-article-cat">{a.catalog_id}</span>}
            </span>
          )}
        />

        {selectedArticle && (
          <div className="mng-edit-block">

            <div className="mng-edit-row">
              <div className="mng-field mng-field--grow">
                <label className="mng-label">Код/назва у постачальника</label>
                <input
                  className="mng-input"
                  value={fields.new_code}
                  onChange={e => setField('new_code', e.target.value)}
                  placeholder="Наприклад: №  CW2289-001 Nike Air Force 1 Mid '07 Black"
                />
                <div className="mng-desc" style={{ fontSize: 12, marginTop: 4 }}>
                  Постачальник змінив код/назву товару у своєму листі — правите тут, запишеться і в лист поставщика, і в ALLDROP ART, і до нас у базу
                </div>
              </div>
            </div>

            <div className="mng-edit-row">
              <div className="mng-field mng-field--grow">
                <label className="mng-label">Артикул каталога</label>
                <input
                  className="mng-input"
                  value={fields.catalog_id}
                  onChange={e => setField('catalog_id', e.target.value)}
                  placeholder="Например: ALL24111"
                />
              </div>
            </div>

            <div className="mng-edit-row mng-edit-row--photo">
              <div className="mng-field mng-field--grow">
                <label className="mng-label">Фото URL (прямая ссылка)</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    className="mng-input"
                    style={{ flex: 1 }}
                    value={fields.photo_url}
                    onChange={e => setField('photo_url', e.target.value)}
                    placeholder="https://images2.imgbox.com/..."
                  />
                  <button
                    type="button"
                    className="mng-btn"
                    style={{ whiteSpace: 'nowrap', padding: '6px 12px', fontSize: 13 }}
                    disabled={photoUploading}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {photoUploading ? 'Загрузка…' : '📎 Файл'}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handlePhotoUpload}
                  />
                </div>
              </div>
              {hasPhoto && (
                <div className="mng-photo-preview">
                  <img src={proxyImageUrl(fields.photo_url)} alt="" />
                </div>
              )}
            </div>

            <div className="mng-edit-row">
              <div className="mng-field mng-field--grow">
                <label className="mng-label">Ссылка TG (фото)</label>
                <input
                  className="mng-input"
                  value={fields.photo_link}
                  onChange={e => setField('photo_link', e.target.value)}
                  placeholder="https://t.me/..."
                />
              </div>
            </div>

          </div>
        )}

        {error && <div className="mng-error">{error}</div>}

        {result && (
          <div className="mng-success">
            <span>Задача #{result.job_id} в очереди</span>
            {result.changed && Object.keys(result.changed).map(k => (
              <span key={k} className="mng-success-field">{k}: {result.changed[k] || '(пусто)'}</span>
            ))}
          </div>
        )}

        <button
          className="mng-btn"
          type="submit"
          disabled={loading || !supplier || !selectedArticle}
        >
          {loading ? 'Сохранение…' : 'Сохранить изменения'}
        </button>

      </form>
        )}
    </div>
  );
}
